import { Component, OnInit, NgZone, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { MinMaxDate } from '../Models/MiscModels';
import { GoogleChartService } from '../services/google-chart.service';
import { ActivatedRoute } from '@angular/router';
import { SignalrISOdataService } from '../services/signalr-ISOdata.service';
import { MiscService } from '../services/misc.service';
import { takeUntil } from 'rxjs/operators';
import { lmpTblRow } from '../Models/IsoModels';
import { ResizedEvent } from 'angular-resize-event';

@Component({
  selector: 'app-lmp-chart',
  templateUrl: './lmp-chart.component.html',
  styleUrls: ['./lmp-chart.component.css']
})
export class LmpChartComponent implements OnInit, OnDestroy {

  private destroyed = new Subject<any>();
  private gLib: any;
  private minMaxDate: MinMaxDate;
  private LmpTable: any;
  private LmpDashboard: any;
  private chartTitle: string;
  private LmpLine: any;
  private LmpDateSlider: any;
  private width: number;
  private height: number;
  private controlOptions: any;
  private chartData = new Array<lmpTblRow>();

  constructor(private gChartService: GoogleChartService, private route: ActivatedRoute,
    private signalrService: SignalrISOdataService, private _ngZone: NgZone, private miscSvc: MiscService) {
    this.gLib = this.gChartService.getGoogle();
    this.gLib.charts.load('current', { 'packages': ['corechart', 'table', 'controls'] });
    this.gLib.charts.setOnLoadCallback(this.drawLmpChart.bind(this));
    route.params.pipe(
      takeUntil(this.destroyed)
    ).subscribe(val => {
      this.chartData = this.route.snapshot.data.LmpData;
    });
  }
  private options = {
    enableInteractivity: true,
    series: {
      0: {
        type: 'line',

      },
      1: {
        type: 'bars', color: '#c8393c',

      },

    },
    legend: {
      position: 'top', alignment: 'end'
    },
    titleTextStyle: {
      color: 'black',    // any HTML string color ('red', '#cc00cc')
      fontName: 'Montesserat', // i.e. 'Times New Roman'
      fontSize: '24', // 12, 18 whatever you want (don't specify px)
      bold: false,    // true or false
      italic: true,   // true of false
    },
    chartArea: { width: '80%', height: '80%' },
    crosshair: { trigger: 'both' },
    curveType: 'function',
    lineWidth: 1,
    vAxis: { title: '$/MwH', textStyle: { fontSize: '12' }, format: 'currency' },
    hAxis: {
      textStyle: {
        fontSize: '12'
      },
      gridlines: {
        count: -1,
        units: {
          days: { format: ['MMM dd'] },
          hours: { format: ['HH:mm', 'ha'] },
        }
      },
      minorGridlines: {
        units: {
          hours: { format: ['hh:mm:ss a', 'ha'] },
          minutes: { format: ['HH:mm a Z', ':mm'] }
        }
      },

    },
  };
  setFilterOptions() {
    this.controlOptions = {
      // Filter by the date axis.
      'filterColumnIndex': 0,
      ui: {
        'chartType': 'lineChart',
        'opacity': '0',

        'chartOptions': {

          chartArea: { width: '80%', height: '100%' },
          hAxis: {
            textStyle: {
              fontSize: '14'
            },
            gridlines: {
              count: -1,
              units: {
                days: { format: ['MMM dd'] },
                hours: { format: ['HH:mm', 'ha'] },
              }
            },
            minorGridlines: {
              units: {
                hours: { format: ['hh:mm:ss a', 'ha'] },
                minutes: { format: ['HH:mm a Z', ':mm'] }
              }
            }
          }
        },

      }
    };
  }

  ngOnInit() {
    this.chartData = this.route.snapshot.data.LmpData;

    this.signalrService.LMPmessageReceived
      .pipe(takeUntil(this.destroyed))
      .subscribe((data) => {
        this._ngZone.run(() => {
          this.updateLmpChart(data);
        });
      });
  }
  formatAxes() {
    const date_formatter = new this.gLib.visualization.DateFormat({ pattern: 'MMM dd, yyyy,  h:mm aa' });
    date_formatter.format(this.LmpTable, 0);  // Where 0 is the index of the column

    const formatter = new this.gLib.visualization.NumberFormat({ suffix: ' $/MWH', pattern: '#,###' });
    formatter.format(this.LmpTable, 1); // Apply formatter to second column

    // formatter = new this.gLib.visualization.NumberFormat({ suffix: ' $/MWH', pattern: '#,###' });
    // formatter.format(this.LmpTable, 2); // Apply formatter to third  column

  }





  private drawLmpChart(options) {
    // Create the dataset (DataTable)
    this.LmpTable = new this.gLib.visualization.DataTable();
    this.LmpTable.addColumn('date', 'Date');
    this.LmpTable.addColumn('number', 'LMP');
    this.LmpTable.addColumn('number', 'Hourly Avg. LMP');

    for (let i = 0; i < this.chartData.length; i++) {
      const chartRow = new Array<Date | number>();
      // tslint:disable-next-line:no-shadowed-variable
      const date = new Date(this.chartData[i].timestamp);

      chartRow.push(date);
      if (this.chartData[i].fiveMinuteAvgLMP > 100) {
        this.chartData[i].fiveMinuteAvgLMP = 100;
      }
      chartRow.push(this.chartData[i].fiveMinuteAvgLMP);
      if (this.chartData[i].hourly_Integrated_LMP > 100) {
        this.chartData[i].hourly_Integrated_LMP = 100;
      }
      chartRow.push(this.chartData[i].hourly_Integrated_LMP);

      this.LmpTable.addRow(chartRow);
    }
    const date = new Date(new Date(this.chartData[this.chartData.length - 1].timestamp));

    this.chartTitle = 'PSEG Zone LMP at 5 minute intervals. Last Reading at ' +
      new Date(this.chartData[this.chartData.length - 1].timestamp).toLocaleString() + ' (EST)';
    const MaxDt = new Date(this.chartData[this.chartData.length - 1].timestamp);
    const MinDt = new Date(MaxDt);
    MinDt.setDate(MaxDt.getDate() - 1);

    this.minMaxDate = this.miscSvc.GetMinMaxdate(this.chartData);
    // Create a dashboard.
    const dash_container = document.getElementById('LMPdashboard_div');

    this.LmpDashboard = new this.gLib.visualization.Dashboard(dash_container);

    // Create a date range slider
    this.LmpDateSlider = new this.gLib.visualization.ControlWrapper({
      'controlType': 'ChartRangeFilter',
      'containerId': 'LMPcontrol_div',
      'state': { 'range': { 'start': MinDt, 'end': MaxDt } }
    });
    this.setFilterOptions();
    this.LmpDateSlider.setOptions(this.controlOptions);

    this.formatAxes();

    // Line chart visualization
    this.LmpLine = new this.gLib.visualization.ChartWrapper({
      'chartType': 'ComboChart',
      'containerId': 'LMPline_div',

      'options': this.options,
    });
    this.LmpLine.setOption('title', this.chartTitle);
    // Bind LmpLine to the dashboard, and to the controls
    // this will make sure our line chart is updated when our date changes
    this.LmpDashboard.bind(this.LmpDateSlider, this.LmpLine);

    this.LmpDashboard.draw(this.LmpTable);
  }
  /**
 * Compares two Date objects and returns e number value that represents
 * the result:
 * 0 if the two dates are equal.
 * 1 if the first date is greater than second.
 * -1 if the first date is less than second.
 * @param date1 First date object to compare.
 * @param date2 Second date object to compare.
 */
  public compareDate(date1: Date, date2: Date): number {
    // With Date object we can compare dates them using the >, <, <= or >=.
    // The ==, !=, ===, and !== operators require to use date.getTime(),
    // so we need to create a new instance of Date with 'new Date()'
    const d1 = new Date(date1); const d2 = new Date(date2);

    // Check if the dates are equal
    const same = d1.getTime() === d2.getTime();
    if (same) { return 0; }

    // Check if the first is greater than second
    if (d1 > d2) { return 1; }

    // Check if the first is less than second
    if (d1 < d2) { return -1; }
  }

  private findRowIdx(timestamp: Date): number {
    const i = this.LmpTable.getNumberOfRows() - 1;
    const date = new Date(this.LmpTable.getValue(i, 0));
    const prevHrDate = new Date(date.setHours(date.getHours() - 1));
    for (let i = this.LmpTable.getNumberOfRows() - 1; i >= 0; i--) {
      const prevDate = new Date(this.LmpTable.getValue(i, 0));
      if (this.compareDate(prevDate, prevHrDate) === 0) {
        return i;
      }
    }
    return -1;
  }
  private updateLmpChart(data: lmpTblRow) {

    const date = new Date(new Date(data.timestamp));

    this.chartTitle = 'PSEG Zone LMP at 5 minute intervals. Last Reading at ' +
    date.toLocaleString() + ' (EST)';


    const chartRow = new Array<Date | number>();

    chartRow.push(date);

    if (data.fiveMinuteAvgLMP > 100) {
      data.fiveMinuteAvgLMP = 100;
    }

    chartRow.push(data.fiveMinuteAvgLMP);
    chartRow.push(0);

    if (this.LmpDashboard !== undefined) {

      this.LmpTable.addRow(chartRow);
      const prevHrIdx = this.findRowIdx(date);

      if (prevHrIdx > -1) {
        if (data.hourly_Integrated_LMP > 100) {
          data.hourly_Integrated_LMP = 100;
        }
        this.LmpTable.setValue(prevHrIdx, 2, data.hourly_Integrated_LMP);
      }

      this.LmpLine.setOption('title', this.chartTitle);

      this.formatAxes();
      this.LmpDashboard.draw(this.LmpTable);
    }
  }
  onResized(event: ResizedEvent) {
    this.width = event.newWidth;
    this.height = event.newHeight;


    console.log('width', this.width);
    console.log('height', this.height);

    if (this.LmpLine !== undefined) {
      this.LmpLine.draw();
      this.LmpDateSlider.draw();
    }

  }
  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}

